import { useEffect, useState } from "react";
import { getDateFirstDay4, getDateTimeFirstDay4 } from "../../utils/getDate";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { setRequestStatus } from "../../redux/userSlice";
import ConfirmDialog from "../ConfirmDialog";
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

const UserRequestCard = ({
    requestData,
    allowCancel = false,
    allowPayAccepted = false,
    allowAdminCancelAccepted = false,
    syncUserRequestsInReduxOnAdminCancel = false,
    onAdminRequestUpdated,
}) => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const [italianStatus, setItalianStatus] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [cancelDialogMode, setCancelDialogMode] = useState(null);
    const [isCancelling, setIsCancelling] = useState(false);
    const [cancelError, setCancelError] = useState(null);
    const [optimisticDeleted, setOptimisticDeleted] = useState(false);
    const [payLoading, setPayLoading] = useState(false);
    const [payError, setPayError] = useState(null);

    const effectiveStatus = optimisticDeleted ? 'deleted' : requestData?.status;

    useEffect(() => {
        setOptimisticDeleted(false);
    }, [requestData?.requestId]);

    useEffect(() => {
        if (effectiveStatus) {
            switch (effectiveStatus) {
                case 'picked':
                    setItalianStatus(t("ProfilePage.userRequestCard.statusLabel.picked"))
                    break;
                case 'accepted':
                    setItalianStatus(t("ProfilePage.userRequestCard.statusLabel.accepted"))
                    break;
                case 'declined':
                    setItalianStatus(t("ProfilePage.userRequestCard.statusLabel.declined"))
                    break;
                case 'purchased':
                    setItalianStatus(t("ProfilePage.userRequestCard.statusLabel.purchased"))
                    break;
                case 'deleted':
                    setItalianStatus(t("ProfilePage.userRequestCard.statusLabel.deleted"))
                    break;
                default:
                    break;
            }
        }
    }, [effectiveStatus, i18n.language, t])

    const resetDialog = () => {
        setCancelError(null);
        setConfirmOpen(false);
        setCancelDialogMode(null);
    };

    const closeDialog = () => {
        if (!isCancelling) resetDialog();
    };

    const handleConfirmCancel = async () => {
        const token = localStorage.getItem('ticino-wild-token');
        const requestId = requestData?.requestId;
        if (!token || !requestId) {
            setCancelError(
                cancelDialogMode === 'admin'
                    ? t("ProfilePage.userRequestCard.adminCancelAcceptedError")
                    : t("ProfilePage.userRequestCard.cancelError")
            );
            return;
        }
        setCancelError(null);
        setIsCancelling(true);
        const isAdminFlow = cancelDialogMode === 'admin';
        const url = isAdminFlow
            ? `${process.env.REACT_APP_SERVER_BASE_URL}/requests/admin-cancel-accepted-request/${requestId}`
            : `${process.env.REACT_APP_SERVER_BASE_URL}/requests/delete-request/${requestId}`;
        try {
            const response = await fetch(url, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });
            const body = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(
                    body.message ||
                    (isAdminFlow
                        ? t("ProfilePage.userRequestCard.adminCancelAcceptedError")
                        : t("ProfilePage.userRequestCard.cancelError"))
                );
            }
            setOptimisticDeleted(true);
            if (!isAdminFlow) {
                dispatch(setRequestStatus({ requestId, status: 'deleted' }));
            } else if (syncUserRequestsInReduxOnAdminCancel) {
                dispatch(setRequestStatus({ requestId, status: 'deleted' }));
            }
            if (isAdminFlow && typeof onAdminRequestUpdated === 'function') {
                onAdminRequestUpdated();
            }
            resetDialog();
        } catch (err) {
            setCancelError(
                err.message ||
                (isAdminFlow
                    ? t("ProfilePage.userRequestCard.adminCancelAcceptedError")
                    : t("ProfilePage.userRequestCard.cancelError"))
            );
        } finally {
            setIsCancelling(false);
        }
    };

    const showPickerCancel =
        allowCancel &&
        effectiveStatus === 'picked';

    const showPayAccepted =
        allowPayAccepted &&
        effectiveStatus === 'accepted';

    const showAdminCancel =
        allowAdminCancelAccepted &&
        effectiveStatus === 'accepted';

    const handlePayAccepted = async () => {
        const token = localStorage.getItem('ticino-wild-token');
        const requestId = requestData?.requestId;
        if (!token || !requestId) {
            setPayError(t("ProfilePage.userRequestCard.payRequestError"));
            return;
        }
        setPayError(null);
        setPayLoading(true);
        try {
            const apiUrl = `${process.env.REACT_APP_SERVER_BASE_URL}/requests/activity-request-checkout-url/${requestId}`;
            const response = await fetch(apiUrl, {
                method: 'GET',
                headers: { Authorization: `Bearer ${token}` },
            });
            const body = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(body.message || t("ProfilePage.userRequestCard.payRequestError"));
            }
            const checkoutUrl = body.payload?.url;
            const sessionId = body.payload?.sessionId;
            if (!checkoutUrl || !sessionId) {
                throw new Error(t("ProfilePage.userRequestCard.payRequestError"));
            }
            if (Capacitor.isNativePlatform()) {
                localStorage.setItem('ticino-wild-pending-checkout', JSON.stringify({
                    type: 'activity_request',
                    requestId,
                    sessionId,
                }));
                await Browser.open({ url: checkoutUrl });
            } else {
                window.location.href = checkoutUrl;
            }
        } catch (err) {
            setPayError(err.message || t("ProfilePage.userRequestCard.payRequestError"));
        } finally {
            setPayLoading(false);
        }
    };

    const openPickerDialog = () => {
        setCancelError(null);
        setCancelDialogMode('picker');
        setConfirmOpen(true);
    };

    const openAdminDialog = () => {
        setCancelError(null);
        setCancelDialogMode('admin');
        setConfirmOpen(true);
    };

    const dialogTitle =
        cancelDialogMode === 'admin'
            ? t("ProfilePage.userRequestCard.adminCancelAcceptedTitle")
            : t("ProfilePage.userRequestCard.cancelConfirmTitle");

    const dialogMessage =
        cancelDialogMode === 'admin'
            ? t("ProfilePage.userRequestCard.adminCancelAcceptedMessage")
            : t("ProfilePage.userRequestCard.cancelConfirmMessage");

    const dialogConfirmLabel =
        cancelDialogMode === 'admin'
            ? t("ProfilePage.userRequestCard.adminCancelAcceptedConfirm")
            : t("ProfilePage.userRequestCard.confirmCancel");

    const dialogSendingLabel =
        cancelDialogMode === 'admin'
            ? t("ProfilePage.userRequestCard.adminCancelAcceptedSending")
            : t("ProfilePage.userRequestCard.cancelSending");

    const dialogError =
        cancelError;

    return (
        <>
            <div className="w-full p-2 rounded-xl border border-[#225424] flex flex-col md:justify-between gap-4 md:flex-row md:gap-8">
                {/* Id richiesta */}
                <div className="border-b-2 md:border-r-2 md:pr-2">
                    <h5 className="md:text-end text-xs">{t("ProfilePage.userRequestCard.requestId")}:</h5>
                    <div className="font-bold text-sm text-[#225424]">{requestData?.requestId}</div>
                </div>
                {/* Nome attività */}
                <div className="border-b-2 md:border-r-2 md:pr-2">
                    <h5 className="md:text-end text-xs">{t("ProfilePage.userRequestCard.activityTitle")}:</h5>
                    <Link to={`/activity-details?id=${requestData?.activityId}`}><div className="font-bold text-sm underline">{requestData?.title}</div></Link>
                </div>
                {/* Data richiesta */}
                <div className="border-b-2 md:border-r-2 md:pr-2">
                    <h5 className="md:text-end text-xs">{t("ProfilePage.userRequestCard.requestDate")}:</h5>
                    <div className="font-bold text-sm">{getDateTimeFirstDay4(requestData?.requestDate)}</div>
                </div>
                {/* Data e orario preferiti (solo se presenti) */}
                {requestData?.preferredDate && (
                    <div className="border-b-2 md:border-r-2 md:pr-2">
                        <h5 className="md:text-end text-xs">{t("ProfilePage.userRequestCard.preferredSlot")}:</h5>
                        <div className="font-bold text-sm">
                            {getDateFirstDay4(requestData.preferredDate)}{requestData.preferredTime ? ` · ${requestData.preferredTime}` : ''}
                        </div>
                    </div>
                )}
                {/* Stato */}
                <div className="flex flex-col gap-2 flex-1 min-w-0 md:items-center">
                    <h5 className="text-xs">{t("ProfilePage.userRequestCard.requestStatus")}:</h5>
                    <div className="flex flex-col gap-1 items-start w-full">
                        <div className="flex flex-wrap gap-x-2 gap-y-2 items-center">
                            <div className={`font-bold text-sm ${effectiveStatus === 'picked' ? 'text-[#225424]' : ''} ${effectiveStatus === 'accepted' ? 'text-yellow-500' : ''} ${effectiveStatus === 'declined' ? 'text-red-500' : ''} ${effectiveStatus === 'purchased' ? 'text-green-500' : ''} ${effectiveStatus === 'deleted' ? 'text-neutral-500' : ''}`}>{italianStatus}</div>
                            {showPayAccepted && (
                                <button
                                    type="button"
                                    className="primary text-xs py-1 px-3"
                                    onClick={handlePayAccepted}
                                    disabled={payLoading}
                                >
                                    {payLoading ? t("ProfilePage.userRequestCard.payRequestLoading") : t("ProfilePage.userRequestCard.payRequestButton")}
                                </button>
                            )}
                            {showPickerCancel && (
                                <button
                                    type="button"
                                    className="secondary text-xs"
                                    onClick={openPickerDialog}
                                >
                                    {t("ProfilePage.userRequestCard.cancelRequest")}
                                </button>
                            )}
                            {showAdminCancel && (
                                <button
                                    type="button"
                                    className="secondary text-xs"
                                    onClick={openAdminDialog}
                                >
                                    {t("ProfilePage.userRequestCard.adminCancelAcceptedRequest")}
                                </button>
                            )}
                        </div>
                        {payError && (
                            <p className="text-red-600 text-xs max-w-full">{payError}</p>
                        )}
                    </div>
                </div>
            </div>

            <ConfirmDialog
                isOpen={confirmOpen}
                title={dialogTitle}
                message={dialogMessage}
                cancelLabel={t("ProfilePage.userRequestCard.keepRequest")}
                confirmLabel={dialogConfirmLabel}
                confirmingLabel={dialogSendingLabel}
                error={dialogError}
                onCancel={closeDialog}
                onConfirm={handleConfirmCancel}
                isConfirming={isCancelling}
            />
        </>
    )
}

export default UserRequestCard
